import { FC, memo, useMemo } from 'react'
import { useAppDispatch } from '../../../redux/hooks'
import SearchResultListingPageTransport from '../../../transports/search-result-listing-page-transport'
import { useSearchRegionNode, useSendSearchResultListingDataEvent } from './hooks'
import FooterSection from '../../common/footer'
import HeaderSection from '../header-section'
import { useDateFormatter } from '../../../hooks/date'
import { BreadcrumbList } from '../../../models/common/breadcrumb-model'
import { Offer } from '../../../models/common/offers-model'
import { SuitablesModel } from '../../../models/common/suitables-model'
import { useTranslation } from '../../../hooks/locale'
import SearchResultListingContent from './search-result-listing-content'
import { SearchbarMode } from '../header-section/types'
import FilterHeader from '../../app-components/filter-components/filter-header'
import { FilterHeaderContainer } from './search-result-listing-content/styles'
import { useSetWishlistCount } from '../../../hooks/wishlist'
import { useRequestUserConfigContext } from '../../../hooks/request'
import { useSessionEffect } from '../../../hooks/session'
import { fetchInitialFilters } from '../../../redux/filters'
import { useSmartSearchActive } from '../../app-components/filter-components/hooks'

import { PageWrapper } from './styles'

type Props = {
    data: SearchResultListingPageTransport
    siteRelativeURL: string
    filter: string
}

const SearchResultListingPage: FC<Props> = ({ data, siteRelativeURL, filter: initialFilter }) => {
    const { staticData } = data
    const { filterSection } = staticData
    const { navigationSection, footerSection } = staticData
    const dispatch = useAppDispatch()
    const user = useRequestUserConfigContext()
    const { searchRegionNode } = useSearchRegionNode()
    const isSmartSearchActive = useSmartSearchActive()

    useSessionEffect(() => {
        if (searchRegionNode) {
            dispatch(fetchInitialFilters(user, searchRegionNode?.regionNodeId || 0, true))
        }
    }, [dispatch, user, searchRegionNode?.regionNodeId])

    const { t } = useTranslation()

    const { format } = useDateFormatter()
    const breadcrumbList = useMemo(() => new BreadcrumbList(t), [t])
    const offerModel = useMemo(() => new Offer(t, format), [t, format])
    const suitablesModel = useMemo(() => new SuitablesModel(t), [t])

    useSetWishlistCount()
    useSendSearchResultListingDataEvent()

    return (
        <PageWrapper>
            <HeaderSection
                searchRegionNode={searchRegionNode}
                navigation={navigationSection.navigation}
                siteRelativeURL={siteRelativeURL}
                searchbarMode={SearchbarMode.PILL}
            >
                <FilterHeaderContainer>
                    <FilterHeader
                        filterStaticData={filterSection}
                        wishlistButton={navigationSection.navigation.wishlistButton}
                        showSmartSearch={isSmartSearchActive}
                    />
                </FilterHeaderContainer>
            </HeaderSection>

            <SearchResultListingContent
                initialFilter={initialFilter}
                staticData={staticData}
                siteRelativeURL={siteRelativeURL}
                breadcrumbModel={breadcrumbList}
                offerModel={offerModel}
                suitablesModel={suitablesModel}
                searchRegionNode={searchRegionNode}
            />

            {!!footerSection && (
                <FooterSection
                    footerSection={footerSection}
                    footerLogo={navigationSection.navigation.logo}
                    socialMediaLinks={navigationSection.socialMediaLinks}
                />
            )}
        </PageWrapper>
    )
}

export default memo(SearchResultListingPage)
